import { useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs'
import { Textarea } from '@/components/ui/shadcn/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Avatar } from '@/components/ui/shadcn/avatar'
import type { CreatorInfo } from '@backend/lib/platforms/tiktok'
import { AI_DISCLOSURE_OFF } from '@backend/lib/platforms/aiDisclosure'
import type {
  AiDisclosureChoice,
  AiDisclosurePlatform,
  AiLabelStyle,
  AiProvenanceDetection,
} from '@backend/lib/platforms/aiDisclosure'
import { AiContentLabelSection } from './ai-content-label-section'
import { SrtUploader } from './workspace-settings/srt-uploader'

/**
 * Tab value for the cross-platform settings. Not a platform, and deliberately
 * not styled like one: AI disclosure is a single decision applied to every
 * platform in the post, so presenting it as a peer of "LinkedIn" or "TikTok"
 * would suggest it were per-platform.
 */
const AI_DISCLOSURE_TAB = 'all-platforms'

interface Props {
  postAsCarousel: boolean
  onPostAsCarouselChange: (value: boolean) => void
  publishAsDraft: boolean
  onPublishAsDraftChange: (value: boolean) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  imageCount: number
  videoCount?: number
  /** Workspace ID — required for SRT uploads (file lands in the workspace
   *  S3 scope so it survives reloads and gets swept on workspace deletion). */
  workspaceId?: number
  // Confirmation mode — used when TikTok is active and user clicks Post/Schedule
  confirmMode?: boolean
  onConfirm?: () => void
  confirmLabel?: string
  // YouTube settings
  isYoutubeActive?: boolean
  youtubePrivacy?: 'public' | 'unlisted' | 'private'
  onYoutubePrivacyChange?: (value: 'public' | 'unlisted' | 'private') => void
  youtubePostAsShort?: boolean
  onYoutubePostAsShortChange?: (value: boolean) => void
  youtubeCategory?: string
  onYoutubeCategoryChange?: (value: string) => void
  // Instagram settings
  isInstagramActive?: boolean
  instagramPostAsStory?: boolean
  onInstagramPostAsStoryChange?: (value: boolean) => void
  instagramShareToFeed?: boolean
  onInstagramShareToFeedChange?: (value: boolean) => void
  instagramDisableComments?: boolean
  onInstagramDisableCommentsChange?: (value: boolean) => void
  instagramLocationId?: string
  onInstagramLocationIdChange?: (value: string) => void
  // LinkedIn settings
  isLinkedinActive?: boolean
  linkedinVisibility?: 'PUBLIC' | 'CONNECTIONS'
  onLinkedinVisibilityChange?: (value: 'PUBLIC' | 'CONNECTIONS') => void
  linkedinDisableReshare?: boolean
  onLinkedinDisableReshareChange?: (value: boolean) => void
  /** Workspace S3 URL of an SRT caption file for LinkedIn video posts. */
  linkedinCaptionsUrl?: string
  linkedinCaptionsKey?: string
  /** Atomic update of the URL + S3 key pair. The key is needed so the
   *  uploader can delete the previous file on replace/remove. */
  onLinkedinCaptionsChange?: (next: { url: string; key: string }) => void
  // Facebook settings
  isFacebookActive?: boolean
  facebookPostAsDraft?: boolean
  onFacebookPostAsDraftChange?: (value: boolean) => void
  facebookPostAsStory?: boolean
  onFacebookPostAsStoryChange?: (value: boolean) => void
  facebookPostAsReel?: boolean
  onFacebookPostAsReelChange?: (value: boolean) => void
  facebookPlaceId?: string
  onFacebookPlaceIdChange?: (value: string) => void
  /** Soft demo/geo boost (no audience exclusion). JSON string per Meta's `feed_targeting` schema. */
  facebookFeedTargetingJson?: string
  onFacebookFeedTargetingJsonChange?: (value: string) => void
  /** Hard audience restriction. JSON string per Meta's `targeting` schema. */
  facebookTargetingJson?: string
  onFacebookTargetingJsonChange?: (value: string) => void
  /** Title for FB video posts, separate from the caption. */
  facebookVideoTitle?: string
  onFacebookVideoTitleChange?: (value: string) => void
  /** Watch-ranking signal: BEAUTY_FASHION, MUSIC, SPORTS, etc. */
  facebookContentCategory?: string
  onFacebookContentCategoryChange?: (value: string) => void
  // TikTok settings
  isTiktokActive?: boolean
  tiktokCreatorInfo?: CreatorInfo | null
  isTiktokCreatorInfoLoading?: boolean
  tiktokCreatorInfoError?: string
  tiktokPrivacyLevel?: string
  onTiktokPrivacyLevelChange?: (value: string) => void
  tiktokAllowComments?: boolean
  onTiktokAllowCommentsChange?: (value: boolean) => void
  tiktokAllowDuets?: boolean
  onTiktokAllowDuetsChange?: (value: boolean) => void
  tiktokAllowStitches?: boolean
  onTiktokAllowStitchesChange?: (value: boolean) => void
  // Commercial content disclosure
  tiktokCommercialContent?: boolean
  onTiktokCommercialContentChange?: (value: boolean) => void
  tiktokYourBrand?: boolean
  onTiktokYourBrandChange?: (value: boolean) => void
  tiktokBrandedContent?: boolean
  onTiktokBrandedContentChange?: (value: boolean) => void
  // TikTok publish flow: Direct Post vs Inbox draft
  tiktokPostMode?: 'DIRECT_POST' | 'MEDIA_UPLOAD'
  onTiktokPostModeChange?: (value: 'DIRECT_POST' | 'MEDIA_UPLOAD') => void
  /** One AI-disclosure decision for every platform. See `AiContentLabelSection`. */
  aiDisclosure?: AiDisclosureChoice
  onAiDisclosureChange?: (choice: AiDisclosureChoice) => void
  /** Whether the default came from media provenance rather than the user. */
  aiLabelAutoDetected?: boolean
  /** Whether the post has an image; video can't carry a baked label. */
  aiLabelHasImageMedia?: boolean
  /** The brand's saved label style, and the handlers to set or clear it. */
  /** What we detected in the media, surfaced so the assumption is visible. */
  aiDetection?: AiProvenanceDetection
  aiSavedLabelStyle?: AiLabelStyle | null
  onSaveAiLabelStyle?: (style: AiLabelStyle) => void
  onClearAiLabelStyle?: () => void
  // YouTube metadata
  youtubeDefaultLanguage?: string
  onYoutubeDefaultLanguageChange?: (value: string) => void
  youtubeDefaultAudioLanguage?: string
  onYoutubeDefaultAudioLanguageChange?: (value: string) => void
  youtubeNotifySubscribers?: boolean
  onYoutubeNotifySubscribersChange?: (value: boolean) => void
  youtubeLicense?: 'youtube' | 'creativeCommon'
  onYoutubeLicenseChange?: (value: 'youtube' | 'creativeCommon') => void
  youtubeEmbeddable?: boolean
  onYoutubeEmbeddableChange?: (value: boolean) => void
  youtubePublicStatsViewable?: boolean
  onYoutubePublicStatsViewableChange?: (value: boolean) => void
  /** Per-locale title + description map as a JSON string. Parsed at use-site. */
  youtubeLocalizationsJson?: string
  onYoutubeLocalizationsJsonChange?: (value: string) => void
  /** Workspace S3 URL of an SRT/SBV/VTT caption file. */
  youtubeCaptionsUrl?: string
  youtubeCaptionsKey?: string
  onYoutubeCaptionsChange?: (next: { url: string; key: string }) => void
  youtubeCaptionsLanguage?: string
  onYoutubeCaptionsLanguageChange?: (value: string) => void
  /** Playlist ID to add the uploaded video to. */
  youtubePlaylistId?: string
  onYoutubePlaylistIdChange?: (value: string) => void
  // TikTok auto-add-music (photo carousels only)
  tiktokAutoAddMusic?: boolean
  onTiktokAutoAddMusicChange?: (value: boolean) => void
}

const PRIVACY_LEVEL_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Public',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only me',
}

/** Collapsible "Advanced" section used by every tab to hide newly added
 *  platform-extras fields behind a disclosure. Uses the native <details>
 *  element to avoid pulling in a Radix accordion just for this. Defined at
 *  module scope (not inside the modal component) so React doesn't recreate
 *  the component on every render — which would otherwise reset the
 *  open/closed state every time. */
const AdvancedSection = ({ children }: { children: React.ReactNode }) => (
  <details className="border-border group border-t">
    <summary className="text-muted-foreground hover:text-foreground flex cursor-pointer select-none items-center gap-1.5 py-3 text-xs font-medium uppercase tracking-wide">
      <span className="inline-block transition-transform group-open:rotate-90">›</span>
      Advanced
    </summary>
    <div className="space-y-4 pb-2">{children}</div>
  </details>
)

export function WorkspaceSettingsModal({
  postAsCarousel,
  onPostAsCarouselChange,
  publishAsDraft,
  onPublishAsDraftChange,
  isOpen,
  onOpenChange,
  imageCount,
  workspaceId,
  confirmMode = false,
  onConfirm,
  confirmLabel = 'Confirm & Publish',
  isYoutubeActive = false,
  youtubePrivacy = 'public',
  onYoutubePrivacyChange,
  youtubePostAsShort = false,
  onYoutubePostAsShortChange,
  youtubeCategory = '',
  onYoutubeCategoryChange,
  isInstagramActive = false,
  instagramPostAsStory = false,
  onInstagramPostAsStoryChange,
  instagramShareToFeed = true,
  onInstagramShareToFeedChange,
  instagramDisableComments = false,
  onInstagramDisableCommentsChange,
  instagramLocationId = '',
  onInstagramLocationIdChange,
  isLinkedinActive = false,
  linkedinVisibility = 'PUBLIC',
  onLinkedinVisibilityChange,
  linkedinDisableReshare = false,
  onLinkedinDisableReshareChange,
  linkedinCaptionsUrl = '',
  linkedinCaptionsKey = '',
  onLinkedinCaptionsChange,
  isFacebookActive = false,
  facebookPostAsDraft = false,
  onFacebookPostAsDraftChange,
  facebookPostAsStory = false,
  onFacebookPostAsStoryChange,
  facebookPostAsReel = false,
  onFacebookPostAsReelChange,
  facebookPlaceId = '',
  onFacebookPlaceIdChange,
  facebookFeedTargetingJson = '',
  onFacebookFeedTargetingJsonChange,
  facebookTargetingJson = '',
  onFacebookTargetingJsonChange,
  facebookVideoTitle = '',
  onFacebookVideoTitleChange,
  facebookContentCategory = '',
  onFacebookContentCategoryChange,
  isTiktokActive = false,
  tiktokCreatorInfo,
  isTiktokCreatorInfoLoading = false,
  tiktokCreatorInfoError,
  tiktokPrivacyLevel,
  onTiktokPrivacyLevelChange,
  tiktokAllowComments = false,
  onTiktokAllowCommentsChange,
  tiktokAllowDuets = false,
  onTiktokAllowDuetsChange,
  tiktokAllowStitches = false,
  onTiktokAllowStitchesChange,
  tiktokCommercialContent = false,
  onTiktokCommercialContentChange,
  tiktokYourBrand = false,
  onTiktokYourBrandChange,
  tiktokBrandedContent = false,
  onTiktokBrandedContentChange,
  tiktokPostMode = 'DIRECT_POST',
  onTiktokPostModeChange,
  aiDisclosure = AI_DISCLOSURE_OFF,
  onAiDisclosureChange,
  aiLabelAutoDetected = false,
  aiLabelHasImageMedia = false,
  aiDetection = 'none',
  aiSavedLabelStyle,
  onSaveAiLabelStyle,
  onClearAiLabelStyle,
  youtubeDefaultLanguage = '',
  onYoutubeDefaultLanguageChange,
  youtubeDefaultAudioLanguage = '',
  onYoutubeDefaultAudioLanguageChange,
  youtubeNotifySubscribers = true,
  onYoutubeNotifySubscribersChange,
  youtubeLicense = 'youtube',
  onYoutubeLicenseChange,
  youtubeEmbeddable = true,
  onYoutubeEmbeddableChange,
  youtubePublicStatsViewable = true,
  onYoutubePublicStatsViewableChange,
  youtubeLocalizationsJson = '',
  onYoutubeLocalizationsJsonChange,
  youtubeCaptionsUrl = '',
  youtubeCaptionsKey = '',
  onYoutubeCaptionsChange,
  youtubeCaptionsLanguage = '',
  onYoutubeCaptionsLanguageChange,
  youtubePlaylistId = '',
  onYoutubePlaylistIdChange,
  tiktokAutoAddMusic = false,
  onTiktokAutoAddMusicChange,
  videoCount = 0,
}: Props) {
  const hasMultipleImages = imageCount > 1

  // TikTok routes all-image media to the photo endpoint and a lone video to
  // the video endpoint (see tiktok/lib/postContent.ts). Duet and Stitch don't
  // exist for photo posts, and the Content Sharing Guidelines require that a
  // photo post expose ONLY the Comments toggle.
  const isTiktokPhotoPost = imageCount > 0 && videoCount === 0

  // Force `postAsCarousel` to false only when the workspace transitions from
  // multi-image to no-longer-multi-image (e.g. user removes images). Without
  // the transition gate the clamp would also fire on initial mount of an
  // empty workspace and overwrite the "default true for new workspaces"
  // hydration value before the user ever adds media.
  const prevHadMultipleImagesRef = useRef(hasMultipleImages)
  useEffect(() => {
    if (prevHadMultipleImagesRef.current && !hasMultipleImages && postAsCarousel) {
      onPostAsCarouselChange(false)
    }
    prevHadMultipleImagesRef.current = hasMultipleImages
  }, [hasMultipleImages, postAsCarousel, onPostAsCarouselChange])

  // 3b: Branded content cannot have SELF_ONLY privacy
  const isPrivateSelfOnly = tiktokPrivacyLevel === 'SELF_ONLY'
  const isBrandedContentDisabledByPrivacy = isPrivateSelfOnly

  // When branded content is selected, prevent switching to SELF_ONLY
  const brandedContentBlocksPrivate = tiktokCommercialContent && tiktokBrandedContent

  // Auto-clear branded content if user switches to SELF_ONLY
  useEffect(() => {
    if (isPrivateSelfOnly && tiktokBrandedContent) {
      onTiktokBrandedContentChange?.(false)
    }
  }, [isPrivateSelfOnly, tiktokBrandedContent, onTiktokBrandedContentChange])

  // When commercial content toggle is turned off, clear sub-options
  useEffect(() => {
    if (!tiktokCommercialContent) {
      onTiktokYourBrandChange?.(false)
      onTiktokBrandedContentChange?.(false)
    }
  }, [tiktokCommercialContent, onTiktokYourBrandChange, onTiktokBrandedContentChange])

  // Build the declaration text based on commercial content state (Point 4)
  const declarationNode = (() => {
    const musicLink = (
      <a
        href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
        target="_blank"
        rel="noopener noreferrer"
        className="text-foreground underline">
        Music Usage Confirmation
      </a>
    )
    const brandedPolicyLink = (
      <a
        href="https://www.tiktok.com/legal/page/global/bc-policy/en"
        target="_blank"
        rel="noopener noreferrer"
        className="text-foreground underline">
        Branded Content Policy
      </a>
    )

    if (
      tiktokCommercialContent &&
      (tiktokBrandedContent || (tiktokYourBrand && tiktokBrandedContent))
    ) {
      return (
        <>
          By posting, you agree to TikTok&apos;s {brandedPolicyLink} and {musicLink}
        </>
      )
    }

    return <>By posting, you agree to TikTok&apos;s {musicLink}</>
  })()

  // Filter privacy options — exclude SELF_ONLY when branded content is active
  const filteredPrivacyOptions = (tiktokCreatorInfo?.privacy_level_options ?? []).filter(
    (option) => {
      if (brandedContentBlocksPrivate && option === 'SELF_ONLY') return false
      return true
    },
  )

  // TikTok settings validation for confirm mode
  // Inbox mode hands the post to the creator to finalise in the TikTok app, so
  // a privacy selection here is either discarded (video) or just an editable
  // default (photo). Requiring one would disable the button over a value that
  // never decides anything. Direct Post still requires it — TikTok's guidelines
  // are explicit that the user must choose.
  const tiktokNeedsPrivacyChoice = tiktokPostMode === 'DIRECT_POST'
  const isTiktokSettingsValid =
    !isTiktokActive ||
    ((!tiktokNeedsPrivacyChoice || !!tiktokPrivacyLevel) &&
      (!tiktokCommercialContent || tiktokYourBrand || tiktokBrandedContent))

  // In confirm mode, default to TikTok tab since that's what needs confirmation.
  // Otherwise open on the cross-platform tab: AI disclosure is one decision for
  // the whole post, and it used to sit above the tab bar where it was always
  // visible. Moving it into a tab would otherwise hide a compliance control
  // behind a click.
  const defaultTab = confirmMode ? 'tiktok' : AI_DISCLOSURE_TAB

  // Only platforms selected for this post get an explanation line.
  const aiLabelPlatforms: AiDisclosurePlatform[] = [
    ...(isInstagramActive ? (['instagram'] as const) : []),
    ...(isFacebookActive ? (['facebook'] as const) : []),
    ...(isLinkedinActive ? (['linkedin'] as const) : []),
    ...(isTiktokActive ? (['tiktok'] as const) : []),
    ...(isYoutubeActive ? (['youtube'] as const) : []),
  ]

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {/* Wider than the dialog default (`max-w-lg`): the sidebar takes a fixed
          strip off the left, and the per-platform explanations are full
          sentences that wrapped every few words in the remaining column. */}
      <DialogContent className="flex h-[85vh] max-h-[800px] max-w-4xl flex-col p-8">
        <DialogHeader>
          <DialogTitle>{confirmMode ? 'Review TikTok Settings' : 'Settings'}</DialogTitle>
          <DialogDescription>
            {confirmMode
              ? 'Please review your TikTok posting settings before publishing'
              : 'Configure your posting preferences'}
          </DialogDescription>
        </DialogHeader>

        {/*
          Vertical nav rather than one tab strip. AI disclosure applies to the
          whole post, so listing it alongside "LinkedIn" and "TikTok" read as if
          it were another platform. Grouping under headings makes the difference
          structural instead of a styling hint.
        */}
        <Tabs
          defaultValue={defaultTab}
          orientation="vertical"
          className="flex min-h-0 flex-1 flex-row gap-4">
          <TabsList className="h-auto w-40 shrink-0 flex-col items-stretch justify-start gap-0.5 bg-transparent p-0">
            <p className="text-muted-foreground px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide">
              All platforms
            </p>
            <TabsTrigger
              value={AI_DISCLOSURE_TAB}
              className="data-[state=active]:bg-hover justify-start gap-1.5">
              AI content
              {/* State indicator, so the decision stays legible from a platform
                  tab. Without it, moving this out of the always-visible header
                  would hide whether disclosure is on at all. */}
              {(aiDisclosure?.nativeLabel || aiDisclosure?.visibleLabel) && (
                <span aria-hidden className="bg-primary size-1.5 rounded-full" />
              )}
              <span className="sr-only">
                {aiDisclosure?.nativeLabel || aiDisclosure?.visibleLabel ? '(on)' : '(off)'}
              </span>
            </TabsTrigger>
            <p className="text-muted-foreground px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide">
              Platforms
            </p>
            {isLinkedinActive && (
              <TabsTrigger value="linkedin" className="data-[state=active]:bg-hover justify-start">
                LinkedIn
              </TabsTrigger>
            )}
            {isInstagramActive && (
              <TabsTrigger value="instagram" className="data-[state=active]:bg-hover justify-start">
                Instagram
              </TabsTrigger>
            )}
            {isTiktokActive && (
              <TabsTrigger value="tiktok" className="data-[state=active]:bg-hover justify-start">
                TikTok
              </TabsTrigger>
            )}
            {isYoutubeActive && (
              <TabsTrigger value="youtube" className="data-[state=active]:bg-hover justify-start">
                YouTube
              </TabsTrigger>
            )}
            {isFacebookActive && (
              <TabsTrigger value="facebook" className="data-[state=active]:bg-hover justify-start">
                Facebook
              </TabsTrigger>
            )}
          </TabsList>

          {/* `min-w-0` is load-bearing: a flex item defaults to `min-width:
              auto`, so any wide descendant (a long JSON placeholder, an SRT
              filename) pushes this column past the dialog and the whole modal
              scrolls sideways. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {/* Cross-platform tab: one disclosure decision for the whole post. */}
            <TabsContent value={AI_DISCLOSURE_TAB} className="overflow-y-auto">
              <div className="py-2">
                <AiContentLabelSection
                  choice={aiDisclosure ?? AI_DISCLOSURE_OFF}
                  onChoiceChange={(next) => onAiDisclosureChange?.(next)}
                  activePlatforms={aiLabelPlatforms}
                  hasImageMedia={aiLabelHasImageMedia}
                  autoDetected={aiLabelAutoDetected}
                  detected={aiDetection}
                  savedStyle={aiSavedLabelStyle}
                  onSaveStyleAsDefault={onSaveAiLabelStyle}
                  onClearSavedStyle={onClearAiLabelStyle}
                />
              </div>
            </TabsContent>

            {/* LinkedIn Tab */}
            {isLinkedinActive && (
              <TabsContent value="linkedin" className="overflow-y-auto">
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="post-as-carousel"
                        checked={postAsCarousel && hasMultipleImages}
                        disabled={!hasMultipleImages}
                        onCheckedChange={(checked: boolean) => {
                          if (hasMultipleImages) {
                            onPostAsCarouselChange(checked)
                          }
                        }}
                      />
                      <Label
                        htmlFor="post-as-carousel"
                        className={`text-sm font-medium ${
                          !hasMultipleImages ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        When many files, post as carousel
                      </Label>
                    </div>
                    {!hasMultipleImages && (
                      <p className="text-muted-foreground pl-7 text-xs text-amber-500">
                        This option is only available when you have multiple images (2 or more).
                      </p>
                    )}
                    <p className="text-muted-foreground pl-7 text-xs">
                      When you have multiple images, you can post them as a carousel (swipeable
                      document) or as separate image posts.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="linkedin-save-as-draft"
                        checked={publishAsDraft}
                        onCheckedChange={(checked: boolean) => {
                          onPublishAsDraftChange(checked)
                        }}
                      />
                      <Label
                        htmlFor="linkedin-save-as-draft"
                        className="cursor-pointer text-sm font-medium">
                        Post as draft
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Your post will be saved as a draft on LinkedIn instead of being published
                      immediately. You can review and publish it later from your LinkedIn account.
                    </p>
                  </div>

                  {/* Visibility */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="linkedin-visibility" className="text-sm font-medium">
                      Visibility
                    </Label>
                    <Select
                      value={linkedinVisibility}
                      onValueChange={(value) =>
                        onLinkedinVisibilityChange?.(value as 'PUBLIC' | 'CONNECTIONS')
                      }>
                      <SelectTrigger id="linkedin-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PUBLIC">Public</SelectItem>
                        <SelectItem value="CONNECTIONS">Connections only</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Choose who can see your post on LinkedIn.
                    </p>
                  </div>

                  {/* Disable Resharing */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="linkedin-disable-reshare"
                        checked={linkedinDisableReshare}
                        onCheckedChange={(checked: boolean) => {
                          onLinkedinDisableReshareChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="linkedin-disable-reshare"
                        className="cursor-pointer text-sm font-medium">
                        Disable resharing
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Prevent others from resharing your post on their feed.
                    </p>
                  </div>

                  <AdvancedSection>
                    {/* Captions (video posts only) */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="linkedin-captions-upload" className="text-sm font-medium">
                        Caption file (SRT)
                      </Label>
                      <SrtUploader
                        inputId="linkedin-captions-upload"
                        url={linkedinCaptionsUrl}
                        key_={linkedinCaptionsKey}
                        workspaceId={workspaceId}
                        onChange={(next) => onLinkedinCaptionsChange?.(next)}
                        accept=".srt"
                      />
                      <p className="text-muted-foreground text-xs">
                        Video posts only. The SRT is uploaded alongside the video and shown in the
                        LinkedIn player. Best-effort — caption-upload failures don&apos;t block the
                        post.
                      </p>
                    </div>
                  </AdvancedSection>
                </div>
              </TabsContent>
            )}

            {/* Instagram Tab */}
            {isInstagramActive && (
              <TabsContent value="instagram" className="overflow-y-auto">
                <div className="space-y-4 py-2">
                  {/* Post as Story */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="instagram-post-as-story"
                        checked={instagramPostAsStory}
                        onCheckedChange={(checked: boolean) => {
                          onInstagramPostAsStoryChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="instagram-post-as-story"
                        className="cursor-pointer text-sm font-medium">
                        Post as Story
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Post your content as a Story instead of a feed post. Stories disappear after
                      24 hours and do not support captions.
                    </p>
                  </div>

                  {/* Share Reel to Feed */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="instagram-share-to-feed"
                        checked={instagramShareToFeed}
                        onCheckedChange={(checked: boolean) => {
                          onInstagramShareToFeedChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="instagram-share-to-feed"
                        className="cursor-pointer text-sm font-medium">
                        Share Reel to feed
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      When posting a video as a Reel, also show it in your main feed. If unchecked,
                      the Reel will only appear under your Reels tab.
                    </p>
                  </div>

                  {/* Disable Comments */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="instagram-disable-comments"
                        checked={instagramDisableComments}
                        onCheckedChange={(checked: boolean) => {
                          onInstagramDisableCommentsChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="instagram-disable-comments"
                        className="cursor-pointer text-sm font-medium">
                        Disable comments
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Prevent others from commenting on your post.
                    </p>
                  </div>

                  <AdvancedSection>
                    {/* Location */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="instagram-location-id" className="text-sm font-medium">
                        Location (Facebook Page ID)
                      </Label>
                      <Input
                        id="instagram-location-id"
                        value={instagramLocationId}
                        onChange={(e) => onInstagramLocationIdChange?.(e.target.value)}
                        placeholder="e.g. 110506962335124"
                      />
                      <p className="text-muted-foreground text-xs">
                        Tag a location on this post. Instagram uses Facebook&apos;s location graph —
                        enter the numeric Page ID of the place. Not supported on stories.
                      </p>
                    </div>
                  </AdvancedSection>
                </div>
              </TabsContent>
            )}

            {/* TikTok Tab */}
            {isTiktokActive && (
              <TabsContent value="tiktok" className="overflow-y-auto">
                <div className="space-y-4 py-2">
                  {/* Creator Info (Point 1) */}
                  {tiktokCreatorInfo && (
                    <div className="bg-muted flex items-center gap-3 rounded-lg p-3">
                      <Avatar className="h-8 w-8">
                        <img
                          src={tiktokCreatorInfo.creator_avatar_url}
                          alt={tiktokCreatorInfo.creator_nickname}
                        />
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">
                          Posting to: {tiktokCreatorInfo.creator_nickname}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          @{tiktokCreatorInfo.creator_username}
                        </p>
                      </div>
                    </div>
                  )}

                  {isTiktokCreatorInfoLoading && (
                    <p className="text-muted-foreground text-sm">
                      Loading TikTok account settings...
                    </p>
                  )}
                  {tiktokCreatorInfoError && (
                    <p className="text-sm text-red-500">
                      Failed to load TikTok settings: {tiktokCreatorInfoError}
                    </p>
                  )}

                  {/* Privacy Level */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="tiktok-privacy" className="text-sm font-medium">
                      Privacy Level
                    </Label>
                    <Select
                      value={tiktokPrivacyLevel || ''}
                      disabled={isTiktokCreatorInfoLoading || !tiktokCreatorInfo}
                      onValueChange={(value) => onTiktokPrivacyLevelChange?.(value)}>
                      <SelectTrigger id="tiktok-privacy">
                        <SelectValue
                          placeholder={
                            isTiktokCreatorInfoLoading
                              ? 'Loading...'
                              : !tiktokCreatorInfo
                                ? 'Connect TikTok account first'
                                : 'Select privacy level'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredPrivacyOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {PRIVACY_LEVEL_LABELS[option] || option}
                          </SelectItem>
                        ))}
                        {brandedContentBlocksPrivate && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-muted-foreground cursor-not-allowed px-2 py-1.5 text-sm opacity-50">
                                  {PRIVACY_LEVEL_LABELS['SELF_ONLY']}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Branded content visibility cannot be set to private.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Allow Comments */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="tiktok-allow-comments"
                        checked={tiktokAllowComments}
                        disabled={tiktokCreatorInfo?.comment_disabled === true}
                        onCheckedChange={(checked: boolean) => {
                          onTiktokAllowCommentsChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="tiktok-allow-comments"
                        className={`text-sm font-medium ${
                          tiktokCreatorInfo?.comment_disabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}>
                        Allow Comments
                      </Label>
                    </div>
                    {tiktokCreatorInfo?.comment_disabled && (
                      <p className="text-muted-foreground pl-7 text-xs text-amber-500">
                        Comments are disabled in your TikTok settings
                      </p>
                    )}
                  </div>

                  {/* Allow Duets — video posts only; TikTok has no duet for photo posts */}
                  {!isTiktokPhotoPost && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="tiktok-allow-duets"
                          checked={tiktokAllowDuets}
                          disabled={tiktokCreatorInfo?.duet_disabled === true}
                          onCheckedChange={(checked: boolean) => {
                            onTiktokAllowDuetsChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="tiktok-allow-duets"
                          className={`text-sm font-medium ${
                            tiktokCreatorInfo?.duet_disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer'
                          }`}>
                          Allow Duets
                        </Label>
                      </div>
                      {tiktokCreatorInfo?.duet_disabled && (
                        <p className="text-muted-foreground pl-7 text-xs text-amber-500">
                          Duets are disabled in your TikTok settings
                        </p>
                      )}
                    </div>
                  )}

                  {/* Allow Stitches — video posts only */}
                  {!isTiktokPhotoPost && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="tiktok-allow-stitches"
                          checked={tiktokAllowStitches}
                          disabled={tiktokCreatorInfo?.stitch_disabled === true}
                          onCheckedChange={(checked: boolean) => {
                            onTiktokAllowStitchesChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="tiktok-allow-stitches"
                          className={`text-sm font-medium ${
                            tiktokCreatorInfo?.stitch_disabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer'
                          }`}>
                          Allow Stitches
                        </Label>
                      </div>
                      {tiktokCreatorInfo?.stitch_disabled && (
                        <p className="text-muted-foreground pl-7 text-xs text-amber-500">
                          Stitches are disabled in your TikTok settings
                        </p>
                      )}
                    </div>
                  )}

                  {/* Commercial Content Disclosure (Point 3) */}
                  <div className="border-border space-y-3 border-t pt-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="tiktok-commercial-content"
                          checked={tiktokCommercialContent}
                          onCheckedChange={(checked: boolean) => {
                            onTiktokCommercialContentChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="tiktok-commercial-content"
                          className="cursor-pointer text-sm font-medium">
                          Content discloses commercial promotion
                        </Label>
                      </div>
                      <p className="text-muted-foreground pl-7 text-xs">
                        Indicate whether this content promotes yourself, a brand, product or
                        service.
                      </p>
                    </div>

                    {tiktokCommercialContent && (
                      <div className="space-y-3 pl-7">
                        {/* Your Brand */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id="tiktok-your-brand"
                              checked={tiktokYourBrand}
                              onCheckedChange={(checked: boolean) => {
                                onTiktokYourBrandChange?.(checked)
                              }}
                            />
                            <Label
                              htmlFor="tiktok-your-brand"
                              className="cursor-pointer text-sm font-medium">
                              Your Brand
                            </Label>
                          </div>
                          <p className="text-muted-foreground pl-7 text-xs">
                            You are promoting yourself or your own business.
                          </p>
                          {tiktokYourBrand && !tiktokBrandedContent && (
                            <p className="pl-7 text-xs text-amber-500">
                              Your photo/video will be labeled as &quot;Promotional content&quot;
                            </p>
                          )}
                        </div>

                        {/* Branded Content */}
                        <div className="space-y-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    id="tiktok-branded-content"
                                    checked={tiktokBrandedContent}
                                    disabled={isBrandedContentDisabledByPrivacy}
                                    onCheckedChange={(checked: boolean) => {
                                      onTiktokBrandedContentChange?.(checked)
                                    }}
                                  />
                                  <Label
                                    htmlFor="tiktok-branded-content"
                                    className={`text-sm font-medium ${
                                      isBrandedContentDisabledByPrivacy
                                        ? 'cursor-not-allowed opacity-50'
                                        : 'cursor-pointer'
                                    }`}>
                                    Branded Content
                                  </Label>
                                </div>
                              </TooltipTrigger>
                              {isBrandedContentDisabledByPrivacy && (
                                <TooltipContent>
                                  <p>
                                    Branded content visibility cannot be private. Change privacy
                                    level first.
                                  </p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                          <p className="text-muted-foreground pl-7 text-xs">
                            You are promoting another brand or a third party.
                          </p>
                          {isBrandedContentDisabledByPrivacy && (
                            <p className="pl-7 text-xs text-amber-500">
                              Branded content can only be posted with Public or Friends visibility.
                            </p>
                          )}
                          {tiktokBrandedContent && (
                            <p className="pl-7 text-xs text-amber-500">
                              Your photo/video will be labeled as &quot;Paid partnership&quot;
                            </p>
                          )}
                        </div>

                        {/* Warning if toggle is on but nothing selected */}
                        {!tiktokYourBrand && !tiktokBrandedContent && (
                          <p className="text-xs text-amber-500">
                            You need to indicate if your content promotes yourself, a third party,
                            or both.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Publishing flow — deliberately NOT inside AdvancedSection. While
                    the TikTok app is unaudited, Direct Post is forced to SELF_ONLY
                    and therefore invisible, which makes inbox mode the only way to
                    get a post seen. Burying the one control that matters most was
                    the wrong default. */}
                  <div className="border-border flex flex-col gap-1.5 border-t pt-3">
                    <Label htmlFor="tiktok-post-mode" className="text-sm font-medium">
                      Publishing flow
                    </Label>
                    <Select
                      value={tiktokPostMode}
                      onValueChange={(value) =>
                        onTiktokPostModeChange?.(value as 'DIRECT_POST' | 'MEDIA_UPLOAD')
                      }>
                      <SelectTrigger id="tiktok-post-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DIRECT_POST">Publish directly</SelectItem>
                        <SelectItem value="MEDIA_UPLOAD">Send to TikTok inbox (draft)</SelectItem>
                      </SelectContent>
                    </Select>
                    {tiktokPostMode === 'DIRECT_POST' ? (
                      <p className="text-muted-foreground text-xs">
                        Posts straight to TikTok using the settings above.
                      </p>
                    ) : isTiktokPhotoPost ? (
                      <p className="text-muted-foreground text-xs">
                        The images land in your TikTok app as a draft. Your caption and the settings
                        above are carried over as defaults you can edit before you post.
                      </p>
                    ) : (
                      <p className="text-xs text-amber-500">
                        The video lands in your TikTok app as a draft. TikTok accepts only the video
                        itself in this mode, so your caption and the settings above are{' '}
                        <strong>not sent</strong> — you set them in the TikTok app before posting.
                      </p>
                    )}
                  </div>

                  <AdvancedSection>
                    {/* Auto-add music (photo carousels only — TikTok ignores it for videos) */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="tiktok-auto-add-music"
                          checked={tiktokAutoAddMusic}
                          onCheckedChange={(checked: boolean) => {
                            onTiktokAutoAddMusicChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="tiktok-auto-add-music"
                          className="cursor-pointer text-sm font-medium">
                          Auto-pick background music
                        </Label>
                      </div>
                      <p className="text-muted-foreground pl-7 text-xs">
                        Photo carousels only — TikTok picks a track from its library. Ignored for
                        video posts.
                      </p>
                    </div>
                  </AdvancedSection>

                  {/* Declaration (Point 4) */}
                  <p className="text-muted-foreground border-border border-t pt-3 text-xs">
                    {declarationNode}
                  </p>
                </div>
              </TabsContent>
            )}

            {/* YouTube Tab */}
            {isYoutubeActive && (
              <TabsContent value="youtube" className="overflow-y-auto">
                <div className="space-y-4 py-2">
                  {/* Privacy */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="youtube-privacy" className="text-sm font-medium">
                      Privacy
                    </Label>
                    <Select
                      value={youtubePrivacy}
                      onValueChange={(value) =>
                        onYoutubePrivacyChange?.(value as 'public' | 'unlisted' | 'private')
                      }>
                      <SelectTrigger id="youtube-privacy">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="unlisted">Unlisted</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Choose who can see your video on YouTube.
                    </p>
                  </div>

                  {/* Post as Short */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="youtube-post-as-short"
                        checked={youtubePostAsShort}
                        onCheckedChange={(checked: boolean) => {
                          onYoutubePostAsShortChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="youtube-post-as-short"
                        className="cursor-pointer text-sm font-medium">
                        Post as Short
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Adds #Shorts to the title so YouTube treats it as a Short. Shorts should be
                      vertical (9:16) and 60 seconds or less.
                    </p>
                  </div>

                  <AdvancedSection>
                    {/* Notify subscribers */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="youtube-notify-subscribers"
                          checked={youtubeNotifySubscribers}
                          onCheckedChange={(checked: boolean) => {
                            onYoutubeNotifySubscribersChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="youtube-notify-subscribers"
                          className="cursor-pointer text-sm font-medium">
                          Notify subscribers
                        </Label>
                      </div>
                      <p className="text-muted-foreground pl-7 text-xs">
                        YouTube emails subscribers and pushes the upload to their feed. Uncheck for
                        soft launches or corrections.
                      </p>
                    </div>

                    {/* Languages */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="youtube-default-language" className="text-sm font-medium">
                          Metadata language
                        </Label>
                        <Input
                          id="youtube-default-language"
                          value={youtubeDefaultLanguage}
                          onChange={(e) => onYoutubeDefaultLanguageChange?.(e.target.value)}
                          placeholder="e.g. en, de, fr"
                        />
                        <p className="text-muted-foreground text-xs">
                          BCP-47 code for title/description.
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label
                          htmlFor="youtube-default-audio-language"
                          className="text-sm font-medium">
                          Audio language
                        </Label>
                        <Input
                          id="youtube-default-audio-language"
                          value={youtubeDefaultAudioLanguage}
                          onChange={(e) => onYoutubeDefaultAudioLanguageChange?.(e.target.value)}
                          placeholder="e.g. en, de, fr"
                        />
                        <p className="text-muted-foreground text-xs">
                          Drives auto-dubbing &amp; captioning.
                        </p>
                      </div>
                    </div>

                    {/* License */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="youtube-license" className="text-sm font-medium">
                        License
                      </Label>
                      <Select
                        value={youtubeLicense}
                        onValueChange={(value) =>
                          onYoutubeLicenseChange?.(value as 'youtube' | 'creativeCommon')
                        }>
                        <SelectTrigger id="youtube-license">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">Standard YouTube License</SelectItem>
                          <SelectItem value="creativeCommon">
                            Creative Commons (Attribution)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Embeddable + public stats */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="youtube-embeddable"
                          checked={youtubeEmbeddable}
                          onCheckedChange={(checked: boolean) => {
                            onYoutubeEmbeddableChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="youtube-embeddable"
                          className="cursor-pointer text-sm font-medium">
                          Allow embedding on external sites
                        </Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="youtube-public-stats"
                          checked={youtubePublicStatsViewable}
                          onCheckedChange={(checked: boolean) => {
                            onYoutubePublicStatsViewableChange?.(checked)
                          }}
                        />
                        <Label
                          htmlFor="youtube-public-stats"
                          className="cursor-pointer text-sm font-medium">
                          Show view/like counts publicly
                        </Label>
                      </div>
                    </div>

                    {/* Localizations */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="youtube-localizations" className="text-sm font-medium">
                        Localizations (JSON)
                      </Label>
                      <Textarea
                        id="youtube-localizations"
                        value={youtubeLocalizationsJson}
                        onChange={(e) => onYoutubeLocalizationsJsonChange?.(e.target.value)}
                        placeholder='e.g. {"de":{"title":"Mein Titel","description":"…"},"fr":{"title":"Mon titre","description":"…"}}'
                        rows={3}
                      />
                      <p className="text-muted-foreground text-xs">
                        Per-locale title &amp; description. Keys are BCP-47 codes. YouTube serves
                        the matching locale based on viewer language.
                      </p>
                    </div>

                    {/* Captions */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="youtube-captions-upload" className="text-sm font-medium">
                        Caption file (SRT / VTT / SBV)
                      </Label>
                      <SrtUploader
                        inputId="youtube-captions-upload"
                        url={youtubeCaptionsUrl}
                        key_={youtubeCaptionsKey}
                        workspaceId={workspaceId}
                        onChange={(next) => onYoutubeCaptionsChange?.(next)}
                        accept=".srt,.vtt,.sbv"
                      />
                      <div className="flex flex-col gap-1.5 pt-1">
                        <Label htmlFor="youtube-captions-language" className="text-sm font-medium">
                          Caption language
                        </Label>
                        <Input
                          id="youtube-captions-language"
                          value={youtubeCaptionsLanguage}
                          onChange={(e) => onYoutubeCaptionsLanguageChange?.(e.target.value)}
                          placeholder="en"
                          className="max-w-[120px]"
                        />
                      </div>
                      <p className="text-muted-foreground text-xs">
                        Captions are uploaded after the video via `captions.insert`. Requires the
                        `youtube.force-ssl` OAuth scope — older connected accounts will need to
                        re-authorize. Best-effort: 403s are logged and skipped.
                      </p>
                    </div>

                    {/* Playlist */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="youtube-playlist-id" className="text-sm font-medium">
                        Add to playlist (ID)
                      </Label>
                      <Input
                        id="youtube-playlist-id"
                        value={youtubePlaylistId}
                        onChange={(e) => onYoutubePlaylistIdChange?.(e.target.value)}
                        placeholder="e.g. PLrAXtmRdnEQy6nuLMHjMZOz59Oq8B3utI"
                      />
                      <p className="text-muted-foreground text-xs">
                        Video is added to this playlist after upload. Same scope requirement as
                        captions — best-effort.
                      </p>
                    </div>
                  </AdvancedSection>

                  {/* Category */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="youtube-category" className="text-sm font-medium">
                      Category
                    </Label>
                    <Select
                      value={youtubeCategory || 'none'}
                      onValueChange={(value) =>
                        onYoutubeCategoryChange?.(value === 'none' ? '' : value)
                      }>
                      <SelectTrigger id="youtube-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (auto)</SelectItem>
                        <SelectItem value="1">Film & Animation</SelectItem>
                        <SelectItem value="2">Autos & Vehicles</SelectItem>
                        <SelectItem value="10">Music</SelectItem>
                        <SelectItem value="15">Pets & Animals</SelectItem>
                        <SelectItem value="17">Sports</SelectItem>
                        <SelectItem value="19">Travel & Events</SelectItem>
                        <SelectItem value="20">Gaming</SelectItem>
                        <SelectItem value="22">People & Blogs</SelectItem>
                        <SelectItem value="23">Comedy</SelectItem>
                        <SelectItem value="24">Entertainment</SelectItem>
                        <SelectItem value="25">News & Politics</SelectItem>
                        <SelectItem value="26">Howto & Style</SelectItem>
                        <SelectItem value="27">Education</SelectItem>
                        <SelectItem value="28">Science & Technology</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground text-xs">
                      Optionally choose a category for your video. If left blank, YouTube will
                      categorize it automatically.
                    </p>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* Facebook Tab */}
            {isFacebookActive && (
              <TabsContent value="facebook" className="overflow-y-auto">
                <div className="space-y-4 py-2">
                  {/* Post as Reel */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="facebook-post-as-reel"
                        checked={facebookPostAsReel}
                        disabled={facebookPostAsStory}
                        onCheckedChange={(checked: boolean) => {
                          onFacebookPostAsReelChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="facebook-post-as-reel"
                        className={`text-sm font-medium ${
                          facebookPostAsStory ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        Post as Reel
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Publish as a Facebook Reel via the dedicated Reels endpoint. Reels need a
                      single vertical video (9:16 recommended, 3-90 seconds). Reels generally get
                      more reach than feed videos for vertical content.
                    </p>
                  </div>

                  {/* Post as Story */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="facebook-post-as-story"
                        checked={facebookPostAsStory}
                        disabled={facebookPostAsReel}
                        onCheckedChange={(checked: boolean) => {
                          onFacebookPostAsStoryChange?.(checked)
                          // Stories don't support drafts. The draft checkbox is
                          // visually disabled below, but the underlying state
                          // would still be `true` if the user had checked draft
                          // before toggling story on — and that stale `true`
                          // would silently flip the draft flag back on if they
                          // ever toggle story off again. Worse, between commit
                          // and post, `facebookSettings.published = !postAsDraft`
                          // would compute as `false` and the publish path would
                          // try to send `published=false` for a story (Meta
                          // ignores it for stories so the post still goes live,
                          // but the user expected a draft and got a live post).
                          // Clear the underlying state to keep the model honest.
                          if (checked) onFacebookPostAsDraftChange?.(false)
                        }}
                      />
                      <Label
                        htmlFor="facebook-post-as-story"
                        className={`text-sm font-medium ${
                          facebookPostAsReel ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        Post as Story
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Publish as a 24-hour ephemeral Page Story instead of a feed post. Stories
                      require a single photo or video — multi-photo and text-only modes aren&apos;t
                      supported. Captions are silently dropped by Facebook for Stories.
                    </p>
                  </div>

                  {/* Post as Draft */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="facebook-post-as-draft"
                        checked={facebookPostAsDraft}
                        disabled={facebookPostAsStory}
                        onCheckedChange={(checked: boolean) => {
                          onFacebookPostAsDraftChange?.(checked)
                        }}
                      />
                      <Label
                        htmlFor="facebook-post-as-draft"
                        className={`text-sm font-medium ${
                          facebookPostAsStory ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        Post as draft
                      </Label>
                    </div>
                    <p className="text-muted-foreground pl-7 text-xs">
                      Your post will be saved as an unpublished draft on the Page instead of going
                      live immediately. You can review and publish it later from Facebook&apos;s
                      Meta Business Suite.
                    </p>
                    {facebookPostAsStory && (
                      <p className="pl-7 text-xs text-amber-500">
                        Stories cannot be saved as drafts.
                      </p>
                    )}
                  </div>

                  <AdvancedSection>
                    {/* Location */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="facebook-place-id" className="text-sm font-medium">
                        Location (Page ID)
                      </Label>
                      <Input
                        id="facebook-place-id"
                        value={facebookPlaceId}
                        onChange={(e) => onFacebookPlaceIdChange?.(e.target.value)}
                        placeholder="e.g. 110506962335124"
                        disabled={facebookPostAsStory || facebookPostAsReel}
                      />
                      <p className="text-muted-foreground text-xs">
                        Tag a location on this post via the numeric Facebook Page ID of the place.
                        Not supported on stories or reels.
                      </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="facebook-video-title" className="text-sm font-medium">
                        Video title
                      </Label>
                      <Input
                        id="facebook-video-title"
                        value={facebookVideoTitle}
                        onChange={(e) => onFacebookVideoTitleChange?.(e.target.value)}
                        placeholder="Shown in Watch surfaces, distinct from caption"
                      />
                      <p className="text-muted-foreground text-xs">Video posts only.</p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="facebook-content-category" className="text-sm font-medium">
                        Video content category
                      </Label>
                      <Select
                        value={facebookContentCategory || 'none'}
                        onValueChange={(value) =>
                          onFacebookContentCategoryChange?.(value === 'none' ? '' : value)
                        }>
                        <SelectTrigger id="facebook-content-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="BEAUTY_FASHION">Beauty &amp; Fashion</SelectItem>
                          <SelectItem value="BUSINESS">Business</SelectItem>
                          <SelectItem value="CARS_TRUCKS">Cars &amp; Trucks</SelectItem>
                          <SelectItem value="COMEDY">Comedy</SelectItem>
                          <SelectItem value="CUTE_ANIMALS">Cute Animals</SelectItem>
                          <SelectItem value="ENTERTAINMENT">Entertainment</SelectItem>
                          <SelectItem value="FAMILY">Family</SelectItem>
                          <SelectItem value="FOOD_HEALTH">Food &amp; Health</SelectItem>
                          <SelectItem value="HOME">Home</SelectItem>
                          <SelectItem value="LIFESTYLE">Lifestyle</SelectItem>
                          <SelectItem value="MUSIC">Music</SelectItem>
                          <SelectItem value="NEWS">News</SelectItem>
                          <SelectItem value="POLITICS">Politics</SelectItem>
                          <SelectItem value="SCIENCE">Science</SelectItem>
                          <SelectItem value="SPORTS">Sports</SelectItem>
                          <SelectItem value="TECHNOLOGY">Technology</SelectItem>
                          <SelectItem value="VIDEO_GAMING">Video Gaming</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Watch-ranking signal for video posts.
                      </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="facebook-feed-targeting" className="text-sm font-medium">
                        Feed targeting (JSON)
                      </Label>
                      <Textarea
                        id="facebook-feed-targeting"
                        value={facebookFeedTargetingJson}
                        onChange={(e) => onFacebookFeedTargetingJsonChange?.(e.target.value)}
                        placeholder='e.g. {"geo_locations":{"countries":["US","CA"]},"age_min":18}'
                        rows={3}
                      />
                      <p className="text-muted-foreground text-xs">
                        Soft demo/geo boost — does not exclude audience. Pure organic-reach upside,
                        no app review required. Free-form JSON per Meta&apos;s schema.
                      </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="facebook-targeting" className="text-sm font-medium">
                        Targeting (JSON)
                      </Label>
                      <Textarea
                        id="facebook-targeting"
                        value={facebookTargetingJson}
                        onChange={(e) => onFacebookTargetingJsonChange?.(e.target.value)}
                        placeholder='e.g. {"geo_locations":{"countries":["US"]},"age_min":21}'
                        rows={3}
                      />
                      <p className="text-muted-foreground text-xs">
                        Hard restriction — audience outside the segment cannot see the post. Use
                        sparingly.
                      </p>
                    </div>
                  </AdvancedSection>
                </div>
              </TabsContent>
            )}
          </div>
        </Tabs>

        {/* Confirm button in confirmation mode */}
        {confirmMode && (
          <div className="border-border flex justify-end border-t pt-4">
            <Button onClick={onConfirm} disabled={!isTiktokSettingsValid}>
              {confirmLabel}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
